import * as Radix from "@radix-ui/react-dropdown-menu";
import type { ReactNode } from "react";

import { cn } from "@/ui/cn";

/**
 * A menu opened from a trigger. Radix handles focus, arrow keys, typeahead and Escape.
 * `trigger` must be a single focusable element (it is rendered `asChild`).
 *
 * Not modal: an item often opens a Dialog, and a modal menu's focus trap and pointer
 * lock then fight the dialog's (a known Radix pitfall that can leave the page
 * unclickable). Outside clicks and Escape still close it.
 */
export function Menu({
  trigger,
  children,
  align = "end",
}: {
  trigger: ReactNode;
  children: ReactNode;
  align?: "start" | "end";
}) {
  return (
    <Radix.Root modal={false}>
      <Radix.Trigger asChild>{trigger}</Radix.Trigger>
      <Radix.Portal>
        <Radix.Content
          align={align}
          sideOffset={4}
          className="z-50 min-w-44 rounded-lg border border-line bg-surface p-1 shadow-pop"
        >
          {children}
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  );
}

export function MenuItem({
  onSelect,
  tone = "default",
  children,
}: {
  onSelect: () => void;
  tone?: "default" | "danger";
  children: ReactNode;
}) {
  return (
    <Radix.Item
      onSelect={onSelect}
      className={cn(
        "flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none",
        "[&_svg]:size-4 data-[highlighted]:bg-sunken",
        tone === "danger" ? "text-danger" : "text-ink",
      )}
    >
      {children}
    </Radix.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return <Radix.Label className="px-2 py-1.5 text-xs text-muted">{children}</Radix.Label>;
}

export function MenuSeparator() {
  return <Radix.Separator className="my-1 h-px bg-line" />;
}
